import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaLeaf, FaHandHoldingUsd, FaFemale, FaSearchLocation, FaHandsHelping } from "react-icons/fa";

const CompanyIntro = () => {
  const { t } = useTranslation();

  const strengths = [
    {
      icon: <FaLeaf />,
      title: t('companyIntro.strengths.sustainableSourcing.title'),
      description: t('companyIntro.strengths.sustainableSourcing.description'),
    },
    {
      icon: <FaHandHoldingUsd />,
      title: t('companyIntro.strengths.fairIncomes.title'),
      description: t('companyIntro.strengths.fairIncomes.description'),
    },
    {
      icon: <FaFemale />,
      title: t('companyIntro.strengths.womensEmpowerment.title'),
      description: t('companyIntro.strengths.womensEmpowerment.description'),
    },
    {
      icon: <FaSearchLocation />,
      title: t('companyIntro.strengths.traceability.title'),
      description: t('companyIntro.strengths.traceability.description'),
    },
    {
      icon: <FaHandsHelping />,
      title: t('companyIntro.strengths.communityDevelopment.title'),
      description: t('companyIntro.strengths.communityDevelopment.description'),
    },
  ];

  return (
    <section className="py-16 bg-white dark:bg-dark-900">
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Main Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              {t('companyIntro.heading.part1')}{" "}
              <span className="text-primary-600 dark:text-primary-400">
                {t('companyIntro.heading.highlight')}
              </span>
            </h2>

            {/* Introduction Text */}
            <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
              <p>
                {t('companyIntro.introduction')}
              </p>
            </div>

            {/* Our Strengths Section */}
            <div className="mt-8">
              <div className="inline-block px-6 py-2 mb-8">
                <h3 className="text-xl font-bold">
                  {t('companyIntro.strengthsTitle')}
                </h3>
              </div>

              {/* Strengths Grid - 5 items in responsive layout */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {strengths.map((strength, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex flex-col items-center text-center"
                  >
                    {/* Icon */}
                    <div className="w-16 h-16 border-2 border-primary-600 dark:border-primary-400 rounded-lg flex items-center justify-center mb-3 bg-white dark:bg-dark-800">
                      <span className="text-3xl text-primary-600 dark:text-primary-400">
                        {strength.icon}
                      </span>
                    </div>

                    {/* Title */}
                    <h4 className="text-xs font-semibold text-primary-600 dark:text-primary-400 mb-1">
                      {strength.title}
                    </h4>

                    {/* Description */}
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-tight">
                      {strength.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Read More Button */}
            <div className="mt-8">
              <Link
                to="/farmer-community"
                className="inline-flex items-center px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
              >
                {t('companyIntro.readMoreButton')}
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative lg:sticky lg:top-24"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[400px] md:h-[450px] lg:h-[500px]">
              <img
                src="/images/about/organic-farming.webp"
                alt={t('companyIntro.imageAlt')}
                className="w-full h-full object-cover"
              />

              {/* Optional: Overlay Badge */}
              <div className="absolute top-4 right-4 bg-white dark:bg-dark-800 px-4 py-2 rounded-full shadow-lg">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🌿</span>
                  <div>
                    <div className="text-xs font-bold text-gray-900 dark:text-white">
                      {t('companyIntro.badge.title')}
                    </div>
                    <div className="text-xs text-primary-600 dark:text-primary-400">
                      {t('companyIntro.badge.subtitle')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CompanyIntro;
